import React, { useState, useEffect } from "react";
import Sidebar from "../Dashboard/Sidebar";
import Header from "../Dashboard/Header";
import Footer from "../Dashboard/Footer";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Save, Calendar, Key, Link as LinkIcon, FileText, Eye, EyeOff,
  Trash2, Github, Loader2, Pencil, Star
} from "lucide-react";
import { FaTags, FaTimes, FaPlus } from 'react-icons/fa';
import CustomAlert from "./CustomAlert";
import { listDefinitions, getArchConfig, setArchConfig, deleteDefinition } from "../../utils/canvasApi";

function ProjectSettings() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  useEffect(() => {
    if (projectId) {
      localStorage.setItem("selectedProjectId", String(projectId));
    }
  }, [projectId]);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeSidebarItem, setActiveSidebarItem] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // state for quality gate profiles
  const [qualityGateProfiles, setQualityGateProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);

  // state for architecture layer definitions
  // Each definition carries an inline `versionSummary` from the list endpoint, so
  // we no longer fetch versions per row here.
  const [archDefinitions, setArchDefinitions] = useState([]);
  const [archSavedDefId, setArchSavedDefId] = useState(null);
  const [archDefsLoading, setArchDefsLoading] = useState(true);

  const [trackedTags, setTrackedTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [project, setProject] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState("success");
  const [projectName, setProjectName] = useState("");
  const [projectLink, setProjectLink] = useState("");
  const [originalData, setOriginalData] = useState({});

  const isLargeScreen = windowWidth >= 992;

  const getSidebarWidth = () => (!sidebarOpen ? "0px" : sidebarCollapsed ? "80px" : "280px");

  const handleSidebarItemClick = (index) => setActiveSidebarItem(index);
  const handleCancel = () => navigate("/project/view");

  const [gh, setGh] = useState({
    loading: true,
    connected: false,
    info: null,
    error: null,
  });

  useEffect(() => {
    document.title = "DCode | Settings";
  }, []);

  // /* Dummy Data Structure (Replace with actual state/fetch logic) */
  // const qualityGateProfiles = [
  //     { id: 1, name: "Default Basic Check", metrics: 2, active: true },
  //     { id: 2, name: "Strict Release Gate", metrics: 4, active: false },
  //     { id: 3, name: "Development Checks", metrics: 1, active: false },
  // ];

  const fetchGithubStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/github/status/?project_id=${projectId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json();

      if (res.ok && data.connected) {
        setGh({ loading: false, connected: true, info: data.integration_info, error: null });
      } else {
        setGh({
          loading: false,
          connected: false,
          info: null,
          error: data.error || data.message || null,
        });
      }
    } catch (e) {
      setGh({ loading: false, connected: false, info: null, error: e.message });
    }
  };

  const handleDisconnectGithub = async () => {
    const ok = window.confirm("Disconnect GitHub for this project?");
    if (!ok) return;

    try {
      const res = await fetch(`${BACKEND_URL}/github/disconnect/?project_id=${projectId}`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed to disconnect GitHub");

      setAlertMessage("Disconnected from GitHub.");
      setAlertType("success");
      setGh({ loading: false, connected: false, info: null, error: null });
    } catch (e) {
      setAlertMessage(e.message);
      setAlertType("danger");
    }
  };

  const fetchQualityGateProfiles = async () => {
    if (!projectId) return;
    setProfilesLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/quality-gate/${projectId}/list-profiles/`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch quality gate profiles.");
      }

      const data = await response.json();
      setQualityGateProfiles(data.data || []);

    } catch (err) {
      console.error("Error fetching quality gate profiles:", err);
      // Optionally set an error state if needed
    } finally {
      setProfilesLoading(false);
    }
  };

  const fetchArchDefinitions = async (pid) => {
    if (!pid) return;
    setArchDefsLoading(true);
    try {
      const [configResult, defsResult] = await Promise.allSettled([
        getArchConfig(pid),
        listDefinitions(pid),
      ]);
      if (configResult.status === 'fulfilled' && configResult.value?.activeDefinitionId) {
        setArchSavedDefId(configResult.value.activeDefinitionId);
      }
      // The list endpoint embeds each definition's versionSummary (count + latest /
      // published status), so a single request fills the whole table.
      const defs = defsResult.status === 'fulfilled' ? (defsResult.value ?? []) : [];
      setArchDefinitions(defs);
    } catch (err) {
      console.error('[ProjectSettings] fetchArchDefinitions failed:', err);
    } finally {
      setArchDefsLoading(false);
    }
  };

  useEffect(() => {
    fetchGithubStatus();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setSidebarOpen(window.innerWidth >= 992);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchProjectDetails = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/projects/${projectId}/`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!response.ok) throw new Error("Failed to fetch project details");

        const data = await response.json();
        const projectData = {
          id: data.data.id,
          name: data.data.project_name,
          link: data.data.github_url || "",
          createdAt: new Date(data.data.created_at).toLocaleDateString(),
          createdTime: new Date(data.data.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          analyzedOn: data.data.analyzed_on ? new Date(data.data.analyzed_on).toLocaleDateString() : null,
          updatedTime: data.data.analyzed_on ? new Date(data.data.analyzed_on).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null,
          apiKey: data.data.api_key || "sk-1234567890abcdef1234567890abcdef",
        };

        setProject(projectData);
        setProjectName(projectData.name);
        setProjectLink(projectData.link);
        const initialTags = (data.data.tracked_tags || []).map((t) => String(t).trim().toLowerCase());
        setTrackedTags(initialTags);
        setOriginalData({ name: projectData.name, link: projectData.link, tracked_tags: initialTags });
      } catch (err) {
        setError(err.message);
      }
    };

    if (projectId) {
      fetchProjectDetails();
      fetchQualityGateProfiles();
      fetchArchDefinitions(projectId);
    }
  }, [BACKEND_URL, projectId]);

  const tagsChanged = () => {
    const orig = originalData.tracked_tags || [];
    const cur = trackedTags || [];
    if (orig.length !== cur.length) return true;
    const oSorted = [...orig].map(String).sort().join(",");
    const cSorted = [...cur].map(String).sort().join(",");
    return oSorted !== cSorted;
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleUserDropdown = () => setUserDropdownOpen(!userDropdownOpen);

  const handleSidebarCollapseChange = (collapsed) => setSidebarCollapsed(collapsed);
  const handleBack = () => navigate("/project/view");

  const handleDeleteProject = async () => {
    if (!projectId) return;
    const ok = window.confirm("Delete this project? This cannot be undone.");
    if (!ok) return;

    try {
      setDeleting(true);
      const resp = await fetch(`${BACKEND_URL}/projects/${projectId}/`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || "Failed to delete project");
      }

      setAlertMessage("Project deleted successfully.");
      setAlertType("success");
      navigate("/project/view");
    } catch (e) {
      setAlertMessage(e.message || "Delete failed.");
      setAlertType("danger");
    } finally {
      setDeleting(false);
    }
  };

  const goToGithubStats = () => {
    setConnecting(true);
    window.location.href = `${BACKEND_URL}/github/install/?project_id=${projectId}`;
    setConnecting(false);
  };

  const handleToggleActive = async (profileId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';

    if (action === 'deactivate') {
      const ok = window.confirm(`Are you sure you want to disable the active profile?`);
      if (!ok) return;
    }

    try {
      // Use the new activate/deactivate endpoint
      const response = await fetch(`${BACKEND_URL}/quality-gate/${projectId}/${profileId}/activate/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to ${action} profile.`);
      }

      setAlertMessage(`Profile successfully ${action}d.`);
      setAlertType("success");
      fetchQualityGateProfiles(); // Refresh the list to update all status badges

    } catch (err) {
      setAlertMessage(err.message);
      setAlertType("danger");
    }
  };

  const handleDeleteProfile = async (profileId, profileName) => {
    const ok = window.confirm(`Are you sure you want to delete the profile "${profileName}"? This action cannot be undone.`);
    if (!ok) return;

    try {
      const response = await fetch(`${BACKEND_URL}/quality-gate/${profileId}/delete/`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to delete profile.");
      }

      setAlertMessage(`Profile "${profileName}" deleted successfully.`);
      setAlertType("success");
      fetchQualityGateProfiles(); // Refresh the list

    } catch (err) {
      setAlertMessage(err.message);
      setAlertType("danger");
    }
  };

  // --- Edit Handler (Navigation) ---

  const handleEditProfile = (profileId) => {
    navigate(`/project/${projectId}/quality-gate/edit/${profileId}`);
  };

  // ── Activation eligibility ──────────────────────────────────────────────────
  // A definition can only be set active once it has a published version, because
  // the backend validates against the latest published version. Drafts can't be
  // validated, so activating a draft-only definition would silently never run.
  const isActivatable = (def) => !!def.versionSummary?.latestPublished;

  // Published, but the latest published version has no enforceable rules — it will
  // "validate" yet report nothing, so we let the user activate it but warn them.
  const isPublishedButRuleless = (def) => {
    const pub = def.versionSummary?.latestPublished;
    return !!pub && (pub.enforceableRuleCount ?? 0) === 0;
  };

  const handleToggleActiveDefinition = async (def) => {
    const isAlreadyActive = def.id === archSavedDefId;

    // Defense in depth — the button is disabled for this case, but never trust the UI.
    if (!isAlreadyActive && !isActivatable(def)) {
      setAlertMessage(`"${def.name}" has no published version. Publish one before setting it active.`);
      setAlertType('warning');
      return;
    }

    try {
      await setArchConfig(projectId, {
        activeDefinitionId: isAlreadyActive ? null : def.id,
        activeVersionId: null,   // floating — backend resolves the latest published version
      });
      setArchSavedDefId(isAlreadyActive ? null : def.id);
    } catch (err) {
      setAlertMessage(err?.message || 'Failed to update active definition.');
      setAlertType('danger');
    }
  };

  const handleDeleteDefinition = async (def) => {
    const ok = window.confirm(`Delete "${def.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteDefinition(projectId, def.id);
      // If we just deleted the active definition, clear the project's arch config
      // too — otherwise it keeps pointing at a deleted definition (a dangling ref).
      if (archSavedDefId === def.id) {
        await setArchConfig(projectId, { activeDefinitionId: null, activeVersionId: null });
        setArchSavedDefId(null);
      }
      setArchDefinitions(prev => prev.filter(d => d.id !== def.id));
      setAlertMessage(`"${def.name}" deleted.`);
      setAlertType('success');
    } catch (err) {
      setAlertMessage(err?.message || 'Failed to delete definition.');
      setAlertType('danger');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        project_name: projectName,
        github_url: projectLink,
        tracked_tags: trackedTags,
      };

      const response = await fetch(`${BACKEND_URL}/projects/${projectId}/`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update project");
      }

      const data = await response.json();
      const savedTags = (data.data.tracked_tags || trackedTags || []).map((t) =>
        String(t).trim().toLowerCase()
      );
      setProject((prev) => ({
        ...prev,
        name: data.data.project_name,
        link: data.data.github_url,
        analyzedOn: data.data.analyzed_on ? new Date(data.data.analyzed_on).toLocaleDateString() : null,
        updatedTime: data.data.analyzed_on ? new Date(data.data.analyzed_on).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null,
      }));
      setTrackedTags(savedTags);
      setOriginalData({
        name: data.data.project_name,
        link: data.data.github_url,
        tracked_tags: savedTags, // Update originalData with the saved tags
      });
      setAlertMessage("Project settings updated successfully!");
      setAlertType("success");
    } catch (err) {
      setAlertMessage(err.message);
      setAlertType("danger");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = () => projectName !== originalData.name || projectLink !== originalData.link || tagsChanged();
  const maskApiKey = (apiKey) =>
    !apiKey ? "" : showApiKey ? apiKey : `${apiKey.substring(0, 7)}...${apiKey.substring(apiKey.length - 4)}`;

  if (error) {
    return (
      <div className="d-flex flex-column min-vh-100">
        <div
          style={{
            marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
            transition: "margin-left 0.4s",
            position: "relative",
            zIndex: 1030,
          }}
        >
          <Header
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
            isCollapsed={sidebarCollapsed}
            userDropdownOpen={userDropdownOpen}
            toggleUserDropdown={toggleUserDropdown}
            pageTitle="Project settings"
          />
        </div>
        <Sidebar
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          activeItem={activeSidebarItem}
          onItemClick={handleSidebarItemClick}
          onCollapseChange={handleSidebarCollapseChange}
        />
        <main
          style={{
            marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
            paddingTop: "70px",
            minHeight: "calc(100vh - 140px)",
          }}
        >
          <div className="container-fluid p-4">
            <div className="text-center text-danger" style={{ minHeight: "60vh" }}>
              <p>Error: {error}</p>
              <button className="btn btn-primary" onClick={handleBack}>
                <ArrowLeft size={16} className="me-2" />
                Back to Projects
              </button>
            </div>
          </div>
        </main>
        <Footer sidebarOpen={sidebarOpen} />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <CustomAlert message={alertMessage} type={alertType} onClose={() => setAlertMessage(null)} />

      <div
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          transition: "margin-left 0.4s",
          position: "relative",
          zIndex: 1030,
        }}
      >
        <Header
          sidebarOpen={sidebarOpen}
          toggleSidebar={toggleSidebar}
          isCollapsed={sidebarCollapsed}
          userDropdownOpen={userDropdownOpen}
          toggleUserDropdown={toggleUserDropdown}
        />
      </div>

      <Sidebar
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        activeItem={activeSidebarItem}
        onItemClick={handleSidebarItemClick}
        onCollapseChange={handleSidebarCollapseChange}
      />

      {sidebarOpen && !isLargeScreen && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1040 }}
          onClick={toggleSidebar}
        />
      )}

      <main
        style={{
          marginLeft: isLargeScreen ? getSidebarWidth() : "0px",
          paddingTop: "70px",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        <div className="container-fluid p-4">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <h2 className="mb-0 text-dark fw-bold">Project settings</h2>
            </div>
            <button
              className="btn d-flex align-items-center"
              style={{ background: "#75c4c0", color: "white" }}
              onClick={handleCancel}
            >
              <ArrowLeft size={18} className="me-1" />
              Back
            </button>
          </div>

          <div className="row">
            <div className="col-lg-8">
              <div className="card shadow-sm mb-3">
                <div className="card-body p-4">
                  <h6 className="card-title setting-title mb-3">Project information</h6>

                  <div className="small text-muted">
                    <p><strong>Project ID:</strong> {project?.id}</p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span className="badge bg-success">Active</span>
                    </p>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        <Key size={16} className="me-2" /> API key
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control setting-input"
                          value={maskApiKey(project?.apiKey)}
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          title={showApiKey ? "Hide" : "Show"}
                        >
                          {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <small className="text-muted">API key cannot be modified</small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        <Calendar size={16} className="me-2" /> Created on
                      </label>
                      <input
                        type="text"
                        className="form-control setting-input"
                        value={`${project?.createdAt} at ${project?.createdTime}`}
                        readOnly
                        style={{ backgroundColor: "#f8f9fa" }}
                      />
                    </div>

                    {project?.analyzedOn && (
                      <div className="mb-3">
                        <label className="form-label fw-semibold">
                          <Calendar size={16} className="me-2" /> Last analyzed on
                        </label>
                        <input
                          type="text"
                          className="form-control setting-input"
                          value={`${project?.analyzedOn} at ${project?.updatedTime}`}
                          readOnly
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      </div>
                    )}

                    {hasChanges() && (
                      <div className="alert alert-info py-2 px-3 mt-3">
                        <small>You have unsaved changes</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>


              <div className="card shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="card-title setting-title mb-0">Quality gate profiles</h6>
                    <button
                      className="btn btn-sm text-white d-flex align-items-center"
                      style={{ background: "#75c4c0", border: "none" }}
                      onClick={() => navigate(`/project/${projectId}/quality-gate-profile`)} // Use projectId for clarity
                    >
                      <FaPlus className="me-2" size={14} />
                      Create New Profile
                    </button>
                  </div>

                  {profilesLoading ? (
                    // --- Loading State ---
                    <div className="text-center py-5 text-muted">
                      <Loader2 size={24} className="animate-spin mb-2 mx-auto" />
                      Fetching profiles...
                    </div>
                  ) : qualityGateProfiles.length === 0 ? (
                    // --- No Profiles Defined State ---
                    // Note: This block is now outside the table to handle the "empty table" requirement cleanly.
                    <div className="alert alert-info text-center py-4">
                      <p className="fw-bold mb-2">No quality gate profiles defined yet.</p>
                      <p className="mb-0">A quality gate profile ensures code meets minimum quality standards, defined by you, before merging.</p>
                      {/* <button
                    className="btn btn-sm mt-3 text-white"
                    style={{ background: "#75c4c0", border: "none" }}
                    onClick={() => navigate(`/project/${projectId}/quality-gate/create`)}
                >
                    <FaPlus className="me-2" size={14} />
                    Start Creating the First Profile
                </button> */}
                    </div>
                  ) : (
                    // --- Profiles Table View ---
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th className="fw-bold">Name</th>
                            <th className="fw-bold">Metric type</th>

                            <th className="fw-bold">Status</th>
                            <th className="fw-bold text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {qualityGateProfiles.map((profile) => (
                            <tr
                              key={profile.id}
                              // Highlight the active profile
                              style={profile.active ? { backgroundColor: '#e6f4f3', borderLeft: '3px solid #75c4c0' } : {}}
                            >
                              <td className="align-middle fw-semibold" style={{ color: profile.active ? '#75c4c0' : 'inherit' }}>
                                {profile.name}
                              </td>
                              <td className="align-middle text-capitalize">
                                {/* Display the core metric type (e.g., Fitness or Smell) */}
                                {profile.metric_type}
                              </td>

                              <td className="align-middle">
                                <span className={`badge ${profile.active ? 'bg-success' : 'bg-secondary'}`}>
                                  {profile.active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="text-end align-middle">
                                <button
                                  className="btn btn-sm btn-outline-secondary me-2"
                                  onClick={() => handleEditProfile(profile.id)}
                                  title="Modify Profile"
                                >
                                  <Pencil size={16} />
                                </button>

                                {/* Toggle Activate/Deactivate Button */}
                                <button
                                  className={`btn btn-sm me-2 ${profile.active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                  onClick={() => handleToggleActive(profile.id, profile.active)}
                                  title={profile.active ? "Set as Inactive" : "Set as Active"}
                                >
                                  {profile.active ? <EyeOff size={16} /> : <Star size={16} />}
                                </button>

                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteProfile(profile.id, profile.name)}
                                  title="Delete Profile"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>


              {/* ── Architecture Layer definitions ── */}
              <div className="card shadow-sm mb-4">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="card-title setting-title mb-0">Architecture definitions</h6>
                      <small className="text-muted">Each definition is a versioned architectural blueprint with named components and dependency rules.</small>
                    </div>
                    <button
                      className="btn btn-sm text-white d-flex align-items-center"
                      style={{ background: "#75c4c0", border: "none" }}
                      onClick={() => navigate('/project/architecture-compliance/new', { state: { projectId } })}
                    >
                      <FaPlus className="me-2" size={14} />
                      Create New Definition
                    </button>
                  </div>

                  {archDefsLoading ? (
                    <div className="text-center py-5 text-muted">
                      <Loader2 size={24} className="animate-spin mb-2 mx-auto" />
                      Fetching definitions...
                    </div>
                  ) : archDefinitions.length === 0 ? (
                    <div className="alert alert-info text-center py-4">
                      <p className="fw-bold mb-2">No architecture compliance definitions yet.</p>
                      <p className="mb-0">An architecture definition maps your codebase into named components with versioned dependency rules.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      {archDefinitions.every(d => !isActivatable(d)) && (
                        <div className="alert alert-warning py-2">
                          No published definitions yet. Publish a version of a definition to enable architecture validation.
                        </div>
                      )}
                      <table className="table table-hover table-sm align-middle">
                        <thead>
                          <tr>
                            <th className="fw-bold">Name</th>
                            <th className="fw-bold">Versions</th>
                            <th className="fw-bold">Latest Version</th>
                            <th className="fw-bold">Status</th>
                            <th className="fw-bold text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...archDefinitions].sort((a, b) => (b.id === archSavedDefId) - (a.id === archSavedDefId)).map((def) => {
                            const versionCount = def.versionSummary?.versionCount ?? 0;
                            const latest = def.versionSummary?.latestVersion;
                            const isActive = def.id === archSavedDefId;

                            const versionBadgeStyle = latest?.status === 'published'
                              ? { background: '#e6f4f3', color: '#0f766e', border: '1px solid #5eead4' }
                              : latest?.status === 'archived'
                                ? { background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }
                                : { background: '#fffbeb', color: '#92400e', border: '1px solid #fcd34d' };

                            const versionIcon = latest?.status === 'published' ? '🔒' : latest?.status === 'archived' ? '📦' : '✏️';

                            return (
                              <tr
                                key={def.id}
                                style={isActive ? { backgroundColor: '#e6f4f3', borderLeft: '3px solid #75c4c0' } : {}}
                              >
                                <td className="fw-semibold" style={{ color: isActive ? '#75c4c0' : 'inherit' }}>
                                  {isActive && <Star size={12} className="me-1" style={{ color: '#f5a623', fill: '#f5a623' }} />}
                                  {def.name}
                                </td>
                                <td className="text-muted" style={{ fontSize: 13 }}>
                                  {versionCount} {versionCount === 1 ? 'version' : 'versions'}
                                </td>
                                <td>
                                  {latest ? (
                                    <span
                                      className="badge rounded-pill"
                                      style={{ ...versionBadgeStyle, fontSize: 11, fontWeight: 600, padding: '4px 10px' }}
                                    >
                                      {versionIcon} v{latest.versionNumber}-{latest.status.charAt(0).toUpperCase() + latest.status.slice(1)}
                                    </span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td>
                                  {isActive ? (
                                    <span className="badge bg-success">Active</span>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                                <td className="text-end align-middle">
                                  <button
                                    className="btn btn-sm btn-outline-secondary me-2"
                                    onClick={() => navigate(`/project/architecture-compliance/${def.id}/edit`, { state: { projectId, definitionName: def.name } })}
                                    title="Edit definition"
                                  >
                                    <Pencil size={16} />
                                  </button>
                                  {(() => {
                                    const activatable = isActivatable(def);
                                    const ruleless = isPublishedButRuleless(def);
                                    const disabled = !isActive && !activatable;
                                    const title = isActive
                                      ? 'Remove as active'
                                      : !activatable
                                        ? 'Publish a version before setting this active'
                                        : ruleless
                                          ? 'Latest published version has no rules — it will validate but report nothing'
                                          : 'Set as active';
                                    return (
                                      <button
                                        className={`btn btn-sm me-2 ${isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                        onClick={() => handleToggleActiveDefinition(def)}
                                        disabled={disabled}
                                        title={title}
                                      >
                                        {isActive ? <EyeOff size={16} /> : <Star size={16} />}
                                      </button>
                                    );
                                  })()}
                                  <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDeleteDefinition(def)}
                                    title="Delete definition"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="col-lg-4">
              <div className="card shadow-sm">
                <div className="card-body p-4">
                  <div className="mb-4">
                    <label htmlFor="projectName" className="form-label fw-semibold">
                      <FileText size={16} className="me-2" /> Project name
                    </label>
                    <input
                      type="text"
                      className="form-control setting-input"
                      id="projectName"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="projectLink" className="form-label fw-semibold">
                      <LinkIcon size={16} className="me-2" /> Project link
                    </label>
                    <input
                      type="url"
                      className="form-control setting-input"
                      id="projectLink"
                      value={projectLink}
                      onChange={(e) => setProjectLink(e.target.value)}
                      placeholder="https://github.com/owner/repo"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="trackedTags" className="form-label fw-semibold">
                      <FaTags className="me-2" /> GitHub tags to track
                    </label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {trackedTags.map((tag, index) => (
                        <span key={index} className="badge d-flex align-items-center" style={{ background: "rgb(117, 196, 192)", border: "rgb(117, 196, 192)", color: "white" }}>
                          {tag}
                          <button
                            type="button"
                            className="btn btn-sm ms-2 p-0"
                            onClick={() =>
                              setTrackedTags(trackedTags.filter((t) => t !== tag))
                            }
                            style={{ background: "none", border: "none", color: "white" }}
                          >
                            <FaTimes size={12} />
                          </button>
                        </span>
                      ))}
                    </div>


                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="e.g., bug, tech-debt"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const tag = newTag.trim().toLowerCase();
                            if (tag && !trackedTags.includes(tag)) {
                              setTrackedTags([...trackedTags, tag]);
                              setNewTag("");
                            }
                          }
                        }}
                      />
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        style={{ background: "rgb(117, 196, 192)", border: "rgb(117, 196, 192)", color: "white" }}
                        onClick={() => {
                          const tag = newTag.trim().toLowerCase();
                          if (tag && !trackedTags.includes(tag)) {
                            setTrackedTags([...trackedTags, tag]);
                            setNewTag("");
                          }
                        }}
                      >
                        <FaPlus />
                      </button>
                    </div>
                  </div>

                  <button
                    className="btn btn-block d-flex align-items-center justify-content-center mb-2"
                    onClick={handleDeleteProject}
                    style={{ color: "#E26D6C", border: "1px solid #E26D6C", padding: "10px" }}
                    disabled={deleting}
                    title="Delete this project"
                    type="button"
                  >
                    {deleting ? (
                      <>
                        <Loader2 size={16} className="me-2 spin" />
                        Deleting…
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} className="me-2" />
                        Delete project
                      </>
                    )}
                  </button>

                  {hasChanges() && (
                    <button
                      className="btn btn-block btn-primary"
                      style={{ background: "#75c4c0", color: "white", border: "none", padding: "10px" }}
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <div className="spinner-border spinner-border-sm me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="me-2" />
                          Save changes
                        </>
                      )}
                    </button>
                  )}

                  <div className="d-block d-lg-none mt-3">
                    {hasChanges() && (
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <div className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={16} className="me-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="card shadow-sm mt-3">
                <div className="card-body p-4">
                  <h6 className="card-title setting-title mb-3">GitHub connection</h6>

                  <div className="d-flex align-items-center mb-3">
                    {gh.loading ? (
                      <span className="badge bg-secondary">Checking…</span>
                    ) : gh.connected ? (
                      <>
                        <span className="badge bg-success me-3">Connected</span>
                        {gh.info?.avatar_url && (
                          <img
                            src={gh.info.avatar_url}
                            alt="Avatar"
                            width={28}
                            height={28}
                            className="rounded-circle me-2"
                          />
                        )}
                        <span className="text-muted">
                          @{gh.info?.github_username} • {gh.info?.repository_count ?? 0} repos
                          {gh.info?.last_sync && (
                            <>
                              {" "}| Last sync: {new Date(gh.info.last_sync).toLocaleString()}
                            </>
                          )}
                          {gh.info?.token_valid === false && (
                            <span className="ms-2 badge bg-warning text-dark">Token expired</span>
                          )}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="badge bg-danger me-2">Not connected</span>
                        {gh.error && <small className="text-muted">({gh.error})</small>}
                      </>
                    )}
                  </div>

                  <div className="d-grid gap-2">
                    {!gh.connected ? (
                      <button
                        className="btn btn-dark d-flex align-items-center justify-content-center"
                        style={{ background: "#75c4c0", color: "white", border: "none", padding: "10px" }}
                        onClick={goToGithubStats}
                        disabled={connecting}
                        title="Connect your GitHub account"
                        type="button"
                      >
                        {connecting ? (
                          <>
                            <Loader2 size={16} className="me-2 spin" />
                            Opening…
                          </>
                        ) : (
                          <>
                            <Github size={16} className="me-2" />
                            Connect to GitHub
                          </>
                        )}
                      </button>
                    ) : (
                      <>
                        {gh.info?.token_valid === false && (
                          <button
                            className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                            onClick={goToGithubStats}
                            type="button"
                            title="Re-authorize GitHub"
                          >
                            <Loader2 size={16} className="me-2" />
                            Reconnect GitHub
                          </button>
                        )}

                        <button
                          className="btn d-flex align-items-center justify-content-center"
                          onClick={handleDisconnectGithub}
                          style={{ color: "#E26D6C", border: "1px solid #E26D6C", padding: "10px" }}
                          title="Disconnect GitHub"
                          type="button"
                        >
                          <Trash2 size={16} className="me-2" />
                          Disconnect GitHub
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer sidebarOpen={sidebarOpen} />
    </div>
  );
}

export default ProjectSettings;